/**
 * components/procurement/MarketFeedsWidget.jsx — US Market Feeds.
 *
 * Live gas (EIA) + electricity-hub (GridStatus) feed cards, each with a
 * Recharts mini-chart that surfaces the rate on hover. Clicking a card opens
 * a larger modal with the full series, axes, hover tooltips and quick stats.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { RefreshCw, TrendingUp, TrendingDown, Flame, Zap, Clock, Maximize2 } from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { getMarketFeeds, refreshMarketFeeds, getMarketJob, getMarketSeries } from '../../api/contracts';

const COMMODITY_ICON = { gas: Flame, electricity: Zap };
const iconColor = (commodity) => (commodity === 'gas' ? 'text-orange-500' : 'text-yellow-500');
const fmtVal = (v, currency) => `${currency === 'USD' ? '$' : ''}${Number(v ?? 0).toFixed(2)}`;
const fmtDay = (p) => (p ? new Date(p).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '');

/** Shared hover tooltip showing rate + date. */
const RateTooltip = ({ active, payload, unit, currency }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md" data-testid="feed-tooltip">
      <div className="font-bold tabular-nums">
        {fmtVal(payload[0].value, currency)}
        <span className="text-muted-foreground font-normal ml-1">{unit}</span>
      </div>
      <div className="text-muted-foreground">{row.period ? new Date(row.period).toLocaleDateString() : ''}</div>
    </div>
  );
};

/** Small in-card chart with hover tooltip. */
const MiniChart = ({ symbol, data, color, unit, currency }) => {
  if (!data || data.length < 2) return null;
  const gid = `spark-${symbol}`;
  return (
    <ResponsiveContainer width="100%" height={48}>
      <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <YAxis hide domain={['dataMin', 'dataMax']} />
        <Tooltip content={<RateTooltip unit={unit} currency={currency} />} cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: '3 3' }} />
        <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill={`url(#${gid})`} dot={false} activeDot={{ r: 3, fill: color }} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const FeedCard = ({ feed, onOpen }) => {
  const Icon = COMMODITY_ICON[feed.commodity] || TrendingUp;
  const up = (feed.change_pct ?? 0) >= 0;
  const pending = feed.status === 'pending' || !feed.has_data;
  const color = up ? '#059669' : '#dc2626';

  return (
    <button
      type="button"
      onClick={() => !pending && onOpen(feed)}
      disabled={pending}
      className={`group relative text-left border rounded-md p-3 transition-all ${
        pending ? 'opacity-70 cursor-default' : 'bg-card hover:border-primary/50 hover:shadow-sm cursor-pointer'
      }`}
      data-testid={`feed-card-${feed.symbol}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <Icon className={`w-5 h-5 ${iconColor(feed.commodity)}`} />
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground truncate">{feed.hub}</span>
          </div>
          <div className="text-[11px] text-muted-foreground truncate">{feed.name}</div>
        </div>
        {pending
          ? <Badge variant="outline" className="text-[10px] shrink-0">Coming soon</Badge>
          : <Maximize2 className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />}
      </div>

      {pending ? (
        <div className="h-14 flex items-center text-xs text-muted-foreground">Feed coming soon</div>
      ) : (
        <>
          <div className="flex items-end justify-between mt-2">
            <div className="text-2xl font-bold tracking-tight tabular-nums">
              {fmtVal(feed.latest_value, feed.currency)}
              <span className="text-[10px] font-normal text-muted-foreground ml-1">{feed.unit}</span>
            </div>
            {feed.change_pct != null && (
              <div className={`flex items-center gap-0.5 text-xs font-semibold ${up ? 'text-emerald-600' : 'text-red-600'}`}>
                {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                {Math.abs(feed.change_pct)}%
              </div>
            )}
          </div>
          <div className="h-12 mt-1">
            <MiniChart symbol={feed.symbol} data={feed.sparkline} color={color} unit={feed.unit} currency={feed.currency} />
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">as of {feed.latest_period}</div>
        </>
      )}
    </button>
  );
};

/** Large modal with the full series + stats. */
const FeedChartModal = ({ feed, accountId, onClose }) => {
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getMarketSeries({ accountId, symbol: feed.symbol, days: 180 })
      .then((d) => { if (alive) setSeries(d); })
      .catch(() => { if (alive) setSeries(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [accountId, feed.symbol]);

  const Icon = COMMODITY_ICON[feed.commodity] || TrendingUp;
  const pts = series?.points || [];
  const values = pts.map((p) => p.value);
  const min = values.length ? Math.min(...values) : null;
  const max = values.length ? Math.max(...values) : null;
  const avg = values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
  const color = feed.commodity === 'gas' ? '#ea580c' : '#ca8a04';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="feed-chart-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${iconColor(feed.commodity)}`} />
            {feed.hub}
            <Badge variant="secondary" className="ml-1 font-normal">{feed.unit}</Badge>
          </DialogTitle>
          <DialogDescription>{feed.name} · last 180 days</DialogDescription>
        </DialogHeader>

        {loading ? (
          <Skeleton className="h-[360px] w-full" />
        ) : pts.length < 2 ? (
          <div className="h-[360px] flex items-center justify-center text-sm text-muted-foreground">No series data available.</div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-3 mb-2">
              {[
                ['Latest', feed.latest_value],
                ['Average', avg],
                ['Low', min],
                ['High', max],
              ].map(([label, val]) => (
                <div key={label} className="border rounded-md p-2.5">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
                  <div className="text-lg font-bold tabular-nums mt-0.5">{fmtVal(val, feed.currency)}</div>
                </div>
              ))}
            </div>
            <div className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={pts} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                  <XAxis dataKey="period" tickFormatter={fmtDay} minTickGap={40} tick={{ fontSize: 11 }} stroke="currentColor" className="text-muted-foreground" />
                  <YAxis tickFormatter={(v) => fmtVal(v, feed.currency)} width={64} tick={{ fontSize: 11 }} domain={['auto', 'auto']} stroke="currentColor" className="text-muted-foreground" />
                  <Tooltip content={<RateTooltip unit={feed.unit} currency={feed.currency} />} />
                  <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 4, fill: color }} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export function MarketFeedsWidget({ accountId, canRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await getMarketFeeds({ accountId });
      setData(d);
    } catch {
      /* silent */
    } finally { setLoading(false); }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { job_id } = await refreshMarketFeeds({ accountId });
      toast.info('Refreshing market feeds…');
      for (let i = 0; i < 12; i += 1) {
        await new Promise((r) => setTimeout(r, 2000));
        const job = await getMarketJob({ accountId, jobId: job_id });
        if (job.status !== 'running') {
          if (job.status === 'success') toast.success(`Feeds updated (${job.rows_upserted} points)`);
          else toast.error('Refresh failed', { description: job.error });
          break;
        }
      }
      await load();
    } catch (e) {
      toast.error('Refresh failed', { description: e?.response?.data?.detail || e.message });
    } finally { setRefreshing(false); }
  };

  return (
    <div className="border rounded-md p-4" data-testid="market-feeds-widget">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold">US Market Feeds</div>
          <div className="text-[11px] text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {data?.last_run_at ? `Updated ${new Date(data.last_run_at).toLocaleString()}` : 'Not yet updated'}
            {data && !data.eia_configured && <span className="text-amber-600 ml-1">· EIA key missing</span>}
            <span className="ml-1 hidden sm:inline">· click any feed to expand</span>
          </div>
        </div>
        {canRefresh && (
          <Button size="sm" variant="outline" onClick={refresh} disabled={refreshing} data-testid="market-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="h-24 flex items-center justify-center text-sm text-muted-foreground">Loading feeds…</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(data?.feeds || []).map((f) => <FeedCard key={f.symbol} feed={f} onOpen={setSelected} />)}
        </div>
      )}

      {selected && <FeedChartModal feed={selected} accountId={accountId} onClose={() => setSelected(null)} />}
    </div>
  );
}

export default MarketFeedsWidget;
